import {DateHelper, Store, StringHelper } from "@bryntum/schedulerpro";
import { createTaskEvent, getIsArchivedForBoard } from "../../other/utils/otherUtils.jsx";
import TaskDrag from "./extendingClasses/TaskDrag.jsx";
import {getWorkingRangesInScheduler} from "../../other/utils/schedulerUtils.jsx";
import {PERSISTENCE_STATE_KEYS} from "../../other/constants/PersistenceStateKeys.jsx";
import EditorBase from "../editors/base/EditorBase.jsx";
import { getSavedBoardSelection } from "../../react/hooks/useBoards.jsx";
import { swivuGridSorterFn } from "../../other/utils/utils.jsx";
import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";
import { userPreferences } from "../../react/context/bryntumContext.jsx";

export class SwivuTaskFinder extends EditorBase {
  static get $name() {
    return "SwivuTaskFinder";
  }

  static get type() {
    return "swivutaskfinder";
  }

  static get configurable() {
    return {
      title: "",
      draggable: true,
      closeAction: "destroy",
      stateId: PERSISTENCE_STATE_KEYS.taskFinderEditor,
      closable: false,
      autoClose: false,
      cls: "swivu-finder minimal-gradient-editor swivu-finder-minimal",
      contentElementCls: "gap-4",
      width: "20rem",
      defaultFocus: (w) => w.ref === "searchField",
      minWidth: "16rem",
      maxWidth: "100vw",
      height: "30rem",
      minHeight: "25rem",
      maxHeight: "100vh",
      layoutStyle: {
        flexDirection: "column",
        justifyContent: "flex-start"
      },
      tbar: {
        cls: "swiviu-finder-toolbar",
        items: {
          searchField: {
            type: "text",
            placeholder: "Task Finder",
            onInput: "up.onSearch",
            onClear: "up.onSearchClear",
            clearable: true,
            flex: 1,
            cls: "swivu-finder-search",
            triggers: {
              searchIcon: {
                cls: 'b-fa b-fa-search pt-[1px]',
                align: "start",
              },
              escButton: {
                // text to this element is assigned from loadRecord/editRecord below
                cls: "text-[0.9em]",
                align: "end",
                handler: "up.close"
              }
            }
          },
        }
      },
      items: [
        {
          ref: "taskFinderGrid",
          type: WIDGET_KEYS.swivuPreferenceStyleGrid,
          flex: 1,
          readOnly: true,
          hideHeaders: true,
          features: {
            cellMenu: false,
            stripe: { disabled: true },
          },
          listeners: {
            cellClick: "up.onGridCellClick",
            selectionChange: "up.onRowSelect"
          },
          columns: [{ id: "task-name", field: "name", type: "unplannedTask", cellCls: "b-sch-horizontal" }],
          bbar: {
            contentElementCls: "!pr-0 !pb-0 items-center flex-col",
            items: [
              {
                ref: "resourceLabel",
                type: "displayfield",
                cls: "text-center",
                width: "100%"
              },
              {
                ref: "submitBtn",
                type: "button",
                text: "",
                color: "b-blue",
                cls: "bg-[transparent] disabled:!text-[#3167a975] text-[#3884e1] border-[1.5px] !rounded-md dark:border-[#373638]",
                hidden: true,
                disabled: true,
                onClick: "up.onSubmit"
              }
            ]
          }
        }
      ]
    }
  }

  editRecord(record, context) {
    super.editRecord(record, context, { isFirstShow: true });
    this.widgetMap.searchField.triggers.escButton.element.innerText = "ESC";
    this.loadRecord(context);
  }

  loadRecord({ project, point, onTaskClick, canInsert = false, canDrag = false,  resourceRecord = null, eventRecord = null, scheduler = null, selectedDate = null, user }) {
    const { submitBtn, taskFinderGrid, bbar, resourceLabel } = this.widgetMap;
    taskFinderGrid.on("cellDblClick", () => {
      onTaskClick({ record: taskFinderGrid.selectedRecord });
      setTimeout(() => {
        this.close();
      }, 100);
    });
    this.scheduler = scheduler;
    this.selectedDate = selectedDate;
    this.canInsert = canInsert;
    const selectedBoard = getSavedBoardSelection();
    this.recordStore = new Store({
      // TODO: given it a modalClass breaks relation defined in the Task model into the actual extra task store causes our relation field not working properly
      // example: enable this line, and open task finder from task grid and then open a task and see all meta field such as creator, updater displays N/A and require refresh
      // modelClass: Task,
      data: project.extraTaskStore.query(
        (entityRecord) => (getIsArchivedForBoard(selectedBoard.slug) || !entityRecord.archived) && !entityRecord.isLinked,
        true
      ),
    });
    this.recordStore.sort("updatedAt", false);
    this.currentBoardUser = project.boardUsersStore.find((boardUser) => boardUser.userId === user.id);
    if (canDrag) {
      new TaskDrag({
        grid: taskFinderGrid,
        schedule: scheduler,
        constrain: false,
        outerElement: taskFinderGrid.element
      })
    }
    // CanInsert will be true if editor is opened from Scheduler's Time Axis
    if (canInsert) {
      this.eventRecord = eventRecord;
      this.resourceRecord = resourceRecord;
      submitBtn.text = eventRecord ? "Replace" : "Insert";
      submitBtn.hidden = false
      resourceLabel.html = eventRecord ?
        `Replace ${eventRecord.task.name || "N/A"} in row ${resourceRecord.name || "N/A"}` :
        `Insert into row ${resourceRecord.name || "N/A"} on ${DateHelper.format(selectedDate, "MMMM DD, YYYY")}`
    } else {
      bbar.destroy();
    }
    this.showBy(point);
    this.loadRecentTasks();
  }

  /*
  * bring user updated task records at the top and sort it by last updated at
  * and then remaining task list then sorted by alphanumerical order
  * */
  sortRecordsByUserAndAlphanumericOrder(records, userId) {
    const selectedBoard = getSavedBoardSelection();
    records = records.filter((record) => {
      return record.id !== this.eventRecord?.taskId && (getIsArchivedForBoard(selectedBoard.slug) || !record.archived) && !record.isLinked;
    })

    /** This logic is to prevent showing the same task event in the task finder if it is already shown in the timeline selected cell */
    const isEnabledDuplicateRecords = StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.enableRedundantTaskEvents, false))
    if (!isEnabledDuplicateRecords && this.canInsert) {
      records = this.preventToShowEventsIfDuplicationMatched(records, this.selectedDate);
    }

    // Separate the records into two groups
    const userUpdatedRecords = [];
    const otherRecords = [];

    records.forEach(record => {
      if (record.lastEventUpdatedBy === userId) {
        userUpdatedRecords.push(record);
      } else {
        otherRecords.push(record);
      }
    });

    // Sort user-updated records by updatedAt in descending order
    userUpdatedRecords.sort((a, b) => new Date(b.lastEventUpdatedAt) - new Date(a.lastEventUpdatedAt));

    // Sort other records by name in alphanumerical order
    otherRecords.sort(swivuGridSorterFn.bind({ field: "name" }));

    // Concatenate the two sorted lists
    return [...userUpdatedRecords, ...otherRecords];
  }

  preventToShowEventsIfDuplicationMatched(records, selectedDate) {
    const tickIndex = this.scheduler.timeAxis.getTickFromDate(selectedDate);
    /** The reason Math.floor
     * Sometime we get tickIndex in points, then if we getAt(decimal value) it's get undefined
     * we need to pass the without decimal value to it */
    const selectedTick = this.scheduler.timeAxis.getAt(Math.floor(tickIndex));
    const tickStartDate = selectedTick.startDate ?? new Date(selectedTick.start);
    const tickEndDate = selectedTick.endDate ??  new Date(selectedTick.end);

    const currentSelectedTimeRangeRecord = this.scheduler.eventStore.getEvents({
      startDate: tickStartDate,
      endDate: tickEndDate,
      resourceRecord: this.resourceRecord,
    })

    return records.filter((record) => {
      return !currentSelectedTimeRangeRecord.some((event) => event.taskId === record.id);
    });
  }

  loadRecentTasks() {
    const { taskFinderGrid } = this.widgetMap;
    const taskRecords = this.recordStore.records;
    const currentUserId = this.currentBoardUser.id;

    taskFinderGrid.store.data = this.sortRecordsByUserAndAlphanumericOrder(taskRecords, currentUserId);
  }

  onSearch({ value }) {
    const { taskFinderGrid } = this.widgetMap;
    taskFinderGrid.store.clearFilters();
    taskFinderGrid.store.filterBy(record => (record.name || "").toLowerCase().includes((value || "").toLowerCase()));
  }

  async onSubmit() {
    const { taskFinderGrid } = this.widgetMap;
    const { selectedRecords } = taskFinderGrid
    // If user is replacing an existing event
    if (this.eventRecord) {
      this.eventRecord.remove();
      taskFinderGrid.selectedRecords.forEach((record, recordIndex) => {
        const newEvent = createTaskEvent({
          schedule: this.scheduler,
          task: record,
          resource: this.resourceRecord,
          date: this.selectedDate,
          eventStartDate: this.eventRecord?.startDate,
          eventEndDate: this.eventRecord?.endDate
        })
        if (recordIndex === 0 && this.eventRecord) this.eventRecord = newEvent;
      })
    } else {
      const eventsToAdd = [], assignmentsToAdd = [];
      const state = this.scheduler.project.crudStores.map(s => { const state = s.store.reapplyFilterOnUpdate; s.store.reapplyFilterOnUpdate = false; return state; })
      const promise = this.scheduler.project.commitAsync();
      getWorkingRangesInScheduler(this.selectedDate, this.scheduler).forEach((dateRange) => {
        selectedRecords.forEach((record) => {
          const newEvent = this.scheduler.eventStore.createRecord({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            taskId: record.id
          });
          const newAssignment = this.scheduler.assignmentStore.createRecord({
            resourceId: this.resourceRecord.id,
            eventId: newEvent.id
          })
          eventsToAdd.push(newEvent);
          assignmentsToAdd.push(newAssignment);
        })
      })
      this.scheduler.eventStore.add(eventsToAdd);
      this.scheduler.assignmentStore.add(assignmentsToAdd);
      await promise;
      this.scheduler.project.crudStores.map((s, index) => {
        s.store.reapplyFilterOnUpdate = state[index]
        s.store.filter();
      })
    }
    this.close();
  }

  onRowSelect({ selection }) {
    const { submitBtn } = this.widgetMap;
    if (!submitBtn.isDestroyed) submitBtn.disabled = !(selection.length)
  }

  onGridCellClick({ event }) {
    const { bbar } = this.widgetMap
    if (!bbar.isDestroyed && event.altKey) {
      this.onSubmit();
    }
  }

  onSearchClear() {
    this.onSearch({ value: "" });
  }
}
